import { UIUtilFormat } from "../../../ui/util/Format";
import { UIUtilViewData } from "../../../ui/util/ViewData";
import { AlumnoEdoCtaUtil } from "../../../ui/utilView/AlumnoEdoCta/AlumnoEdoCtaUtil";
import _L from "../../../util/Labels";
import { Entidad } from "../../Entidad";
import { CFinanzaDetallePagoEnLineaTipoTarjeta, IFinanzaDetallePagoEnLinea } from "../../entidad/FinanzaPagoEnLinea";
import { CFinanzaPagoEnLineaEstado, CFinanzaPagoEnLineaFormaPago, IFinanzaEdoCtaMovimientoDetalle, IFinanzaFolioPagoEnLinea } from "../../entidad/FinanzasEstadoCuenta";
import { _AlumnoEdoCtaObtenerDetallesPagoEnLinea, _DICC_ALUMNO_MOVPAGO_FOLIO } from "../../modulo/FinanzasEstadoCuenta";
import { DModelBase } from "../MBase";
import { MFinanzaEdoCtaMovimiento } from "./MFinanzaEdoCtaMovimiento";

import CCategoriaMov = Entidad.CFinanzaCategoriaMovimiento;
import CTipoValor = Entidad.CFinanzaCargoTipoValor;

export class MFinanzaEdoCtaMovimientoDetalle extends DModelBase<IFinanzaEdoCtaMovimientoDetalle> implements IFinanzaEdoCtaMovimientoDetalle {
    Cargo: string;
    Categoria: CCategoriaMov;
    FechaAplicacion: string;
    FechaMorosidad: string;
    FechaRegistro: string;
    Id: number;
    IdAsignacionCargo: number;
    IdCargo: number;
    IdFacturaFolio: number;
    IdMetodoPago: number;
    Periodicidad: Entidad.CFinanzaCargoPeriodicidadCargos;
    Recargo: number;
    Saldo: number;
    TipoInteres: Entidad.CFinanzaCargoTipoInteres;
    TipoValor: Entidad.CFinanzaCargoTipoValor;
    UUID: string;
    Valor: number;
    FolioPago?: string;
    IdUsuarioCreo?: number;
    IdComision?: number;
    TipoValorComisionPlantilla?: Entidad.CFinanzaCargoTipoValor;
    ValorComisionPlantilla?: number;

    // =================================================
    // PROPIEDADES ADICIONALES
    // =================================================

    private __parentMovimiento: MFinanzaEdoCtaMovimiento = null;
    private __folioPagoEnLineaAsignado?: IFinanzaFolioPagoEnLinea = null;
    private __detallesPagoEnLinea?: IFinanzaDetallePagoEnLinea = null;
    /** Agrupador de movimientos de pagos en línea por folio */
    private __agrupadorMovsPagosFolioPagoOnline = new Map<string, MFinanzaEdoCtaMovimientoDetalle[]>();

    constructor(
        data: IFinanzaEdoCtaMovimientoDetalle,
        parentMovimiento: MFinanzaEdoCtaMovimiento,
    ) {
        super(data);
        this.__parentMovimiento = parentMovimiento;
        this.init();
    }

    // =================================================
    // GETTERS ADICIONALES
    // =================================================

    get ParentMovimiento(): MFinanzaEdoCtaMovimiento {
        return this.__parentMovimiento;
    }

    /** Movimientos relacionados por folio de pago en línea */
    get PagosMovRelacionados(): MFinanzaEdoCtaMovimientoDetalle[] | null {
        if (!this.FolioPago) return null;
        if (!this.__agrupadorMovsPagosFolioPagoOnline) return [];
        const movimientos = this.__agrupadorMovsPagosFolioPagoOnline.get(this.FolioPago);
        return (movimientos || []).filter(mov => mov.Id !== this.Id);
    }
    /** Se usa para pagos online pendientes */
    get FechaVencimiento(): string | null {
        const categoriasPago = [CCategoriaMov.PagoCompleto, CCategoriaMov.PagoParcial];
        if (this.__folioPagoEnLineaAsignado && categoriasPago.includes(this.Categoria)) {
            return this.__folioPagoEnLineaAsignado.FechaVigencia;
        }
        return null;
    }

    get SaldoForTable(): string {
        const detallesParent = this.__parentMovimiento.DetallesYTransacciones;
        if (detallesParent && detallesParent[detallesParent.length - 1] == this) {
            return UIUtilFormat._CurrencyFmt(this.Saldo);
        }
        return "";
    }

    /** Nombre del método de pago */
    get MetodoPagoNombre(): string | null {
        const tipoPagos = [CCategoriaMov.PagoCompleto, CCategoriaMov.PagoParcial];
        if (tipoPagos.includes(this.Categoria)) {
            return AlumnoEdoCtaUtil._GetNombreMetodoPago(this.IdMetodoPago);
        }
        return null;
    }

    /** Detalle del método de pago.
     * * Si el movimiento es un pago en línea conciliado, se muestra el tipo de tarjeta ("Débito"/"Crédito").
     * * Si es un pago en linea sin conciliar o diferente de tarjeta, se muestra la forma de pago ("transferencia bancaria", "Tarjeta de crédito/débito", ...).
     */
    get MetodoPagoDetalle(): string | null {
        if (this.__detallesPagoEnLinea) {
            return {
                [CFinanzaDetallePagoEnLineaTipoTarjeta.Credito]: _L("c_tipotarjeta.credito"),
                [CFinanzaDetallePagoEnLineaTipoTarjeta.Debito]: _L("c_tipotarjeta.debito")
            }[this.__detallesPagoEnLinea.Card.Type] || null;
        }
        else if (this.IdMetodoPago && this.FolioPago) {
            const folioInfo = _DICC_ALUMNO_MOVPAGO_FOLIO.get(this.FolioPago);
            if (folioInfo) {
                return UIUtilViewData._GetStr_FinanzaPagoEnLineaFormaPago(folioInfo.FormaPago);
            }
        }
        return null;
    }

    /**
     * Información completa del método de pago, que incluye el nombre del método de pago y su detalle (si aplica), concatenados en un mismo string.
     * @returns "Openpay - Tarjeta de crédito/débito" | "Openpay - Transferencia bancaria" | etc...
     */
    get MetodoPagoInfoCompleta(): string {
        const nombreMetodoPago = this.MetodoPagoNombre;
        const detalleMetodoPago = this.MetodoPagoDetalle;
        if (nombreMetodoPago && detalleMetodoPago) {
            return `${nombreMetodoPago} - ${detalleMetodoPago}`;
        }
        return nombreMetodoPago || detalleMetodoPago || null;
    }

    get FolioPagoEnLineaRelacionado(): IFinanzaFolioPagoEnLinea | null {
        if (this.__folioPagoEnLineaAsignado) {
            return this.__folioPagoEnLineaAsignado;
        }
        if (this.FolioPago) {
            return _DICC_ALUMNO_MOVPAGO_FOLIO.get(this.FolioPago) || null;
        }
        return null;
    }

    // =================================================
    // MÉTODOS PRIVADOS
    // =================================================

    private init() {
        if (this.Categoria == CCategoriaMov.Comision) {
            const prefTag = UIUtilViewData._GetStr_CategoriaMovimiento(this.Categoria);
            const detalleComision = this.TipoValorComisionPlantilla == CTipoValor.Porcentaje
                ? UIUtilFormat._PercentFmt(this.ValorComisionPlantilla * 100)
                : UIUtilFormat._CurrencyFmt(this.ValorComisionPlantilla)
            this.Cargo = `${prefTag}: ${this.Cargo} (${detalleComision})`
        }
    }

    private _ConsultarDetallesPagoEnLinea(): Promise<IFinanzaDetallePagoEnLinea> {
        return _AlumnoEdoCtaObtenerDetallesPagoEnLinea(this.Id)
            .then(res => {
                this.__detallesPagoEnLinea = null;
                if (res.Resultado > 0 && res.Datos) {
                    this.__detallesPagoEnLinea = res.Datos;
                    return res.Datos;
                }
                return null;
            });
    }

    // =================================================
    // MÉTODOS PÚBLICOS
    // =================================================

    /**
     * Consulta y asigna los detalles del pago en línea al movimiento,
     * si el movimiento es un pago en línea con folio asignado.
     *
     * Esta información se puede consultar desde la propiedad: `FolioPagoEnLineaRelacionado`
     *
     * A su ves alimenta las propiedades: `MetodoPagoDetalle`, `MetodoPagoInfoCompleta`
     */
    public _ConsultarYAsignarDetallesPagoEnLinea(): Promise<IFinanzaDetallePagoEnLinea | null> {
        const folioPago = this.__folioPagoEnLineaAsignado;
        if (!this.FolioPago || !folioPago || folioPago.FormaPago != CFinanzaPagoEnLineaFormaPago.Tarjeta) {
            return Promise.resolve(null);
        }
        if (this.__detallesPagoEnLinea) {
            return Promise.resolve(this.__detallesPagoEnLinea);
        }
        return this._ConsultarDetallesPagoEnLinea();
    }

    /** Asigna el folio de pago en línea al detalle de movimiento. */
    public _AsignarFolioPagoEnLinea(folioPagoEnLinea: IFinanzaFolioPagoEnLinea): void {
        this.__folioPagoEnLineaAsignado = folioPagoEnLinea;
    }

    /**
     * Asigna el agrupador de movimientos de pagos en línea por folio,
     * para su consulta posterior desde la propiedad `PagosMovRelacionados`.
     *
     * @param agrupadorMovsPagosFolioPagoOnline `Map`<`FolioPago`, `MFinanzaEdoCtaMovimientoDetalle[]`>
     */
    public _SetAgrupadorMovimientosPagoEnLinea(agrupadorMovsPagosFolioPagoOnline: Map<string, MFinanzaEdoCtaMovimientoDetalle[]>): void {
        this.__agrupadorMovsPagosFolioPagoOnline = agrupadorMovsPagosFolioPagoOnline;
    }

    public _IsPagoOnlinePendiente(folioPagoInfo?: IFinanzaFolioPagoEnLinea): boolean {
        const esPago = [CCategoriaMov.PagoCompleto, CCategoriaMov.PagoParcial].includes(this.Categoria);
        if (esPago) {
            folioPagoInfo = folioPagoInfo || _DICC_ALUMNO_MOVPAGO_FOLIO.get(this.FolioPago);
            return folioPagoInfo?.EstadoPago == CFinanzaPagoEnLineaEstado.Pendiente;
        }
        return false;
    }
}